// Screen 4 · This Week — the teacher's lessons + the way in to plan one.
// Status chips are neutral; a not-yet-planned lesson is not an error (Design Brief).

import { api } from "../api";
import { esc } from "../dom";
import { navigate } from "../router";

interface WeekLesson {
  id: string;
  topic: string;
  class_name: string;
  status: "not_started" | "in_progress" | "submitted" | "approved";
  plan_id: string | null;
}

interface CalEntry { entry_date: string; label: string; type: "teaching" | "test" | "event" | "holiday" }

const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
function shortDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number) as [number, number, number];
  return `${WD[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]} ${d}`;
}

const chip = (status: WeekLesson["status"]): string =>
  status === "submitted" || status === "approved"
    ? `<span class="chip submitted">Submitted</span>`
    : `<span class="chip">Draft</span>`;

export async function renderThisWeek(app: HTMLElement): Promise<void> {
  const [data, cal] = await Promise.all([
    api<{ week_no: number | null; lessons: WeekLesson[] }>("/api/lessons?scope=week"),
    api<{ entries: CalEntry[] }>("/api/calendar?scope=week"),
  ]);
  const heading = data.week_no ? `Week ${data.week_no}` : "This Week";

  const schoolStrip = cal.entries.length
    ? `<div class="card"><strong>This week at school</strong>${cal.entries
        .map(
          (e) =>
            `<div class="cal-row"><span class="cal-date">${esc(shortDate(e.entry_date))}</span><span class="cal-label">${esc(
              e.label,
            )}</span><span class="chip ${e.type}">${e.type}</span></div>`,
        )
        .join("")}</div>`
    : "";

  const rows = data.lessons.length
    ? data.lessons
        .map((l) => {
          const inner = `<strong>${esc(l.class_name)}</strong> · ${esc(l.topic)} ${chip(l.status)}`;
          return l.plan_id
            ? `<a class="card" data-link href="/plan/${esc(l.plan_id)}">${inner}</a>`
            : `<div class="card">${inner}</div>`;
        })
        .join("")
    : `<p class="empty">Nothing planned yet this week. Plan a lesson when you're ready.</p>`;

  app.innerHTML = `
    <div class="topbar"><a data-link href="/" class="muted">← Home</a></div>
    <h1>${esc(heading)}</h1>
    ${schoolStrip}
    ${rows}
    <div style="height:8px"></div>
    <button class="btn" id="plan" type="button">Plan a lesson</button>`;

  app.querySelector<HTMLButtonElement>("#plan")!.addEventListener("click", () => navigate("/plan/new"));
}
